import type { ErrorResponse } from './errors'

/**
 * Wire-level error class shared between the mobile app and the dashboard.
 * Mirrors the backend's `ErrorResponse` schema and adds the FE-only `type`
 * + `statusCode` for HTTP categorisation.
 *
 * Each consumer app augments this class with its own `getDialogContent`
 * method via TypeScript module augmentation:
 *
 *   // in app code:
 *   import { NetworkError } from '@pulsify/shared/network-error'
 *
 *   declare module '@pulsify/shared/network-error' {
 *     interface NetworkError {
 *       getDialogContent(t: TFunction): { title: string; message: string }
 *     }
 *   }
 *
 *   NetworkError.prototype.getDialogContent = function (t) { ... }
 *
 * The shared class deliberately has no rendering method because the two
 * apps use different i18n systems (i18next vs paraglide). Sharing the
 * class + factory + status mapping covers ~90% of the code; the render
 * step is the only divergent piece.
 */

export type NetworkErrorType =
  | 'UNAUTHORIZED'
  | 'BAD_REQUEST'
  | 'NOT_FOUND'
  | 'TIMEOUT'
  | 'NETWORK_ERROR'
  | 'SERVER_ERROR'
  | 'CANCELLED'
  | 'UNKNOWN'

export const NetworkErrorTypes = {
  UNAUTHORIZED: 'UNAUTHORIZED' as const,
  BAD_REQUEST: 'BAD_REQUEST' as const,
  NOT_FOUND: 'NOT_FOUND' as const,
  TIMEOUT: 'TIMEOUT' as const,
  NETWORK_ERROR: 'NETWORK_ERROR' as const,
  SERVER_ERROR: 'SERVER_ERROR' as const,
  CANCELLED: 'CANCELLED' as const,
  UNKNOWN: 'UNKNOWN' as const,
} as const

export function statusToNetworkErrorType(statusCode: number | undefined): NetworkErrorType {
  switch (statusCode) {
    case undefined:
      return NetworkErrorTypes.UNKNOWN
    case 400:
      return NetworkErrorTypes.BAD_REQUEST
    case 401:
    case 403:
      return NetworkErrorTypes.UNAUTHORIZED
    case 404:
      return NetworkErrorTypes.NOT_FOUND
    default:
      return statusCode >= 500 ? NetworkErrorTypes.SERVER_ERROR : NetworkErrorTypes.UNKNOWN
  }
}

export class NetworkError extends Error {
  type: NetworkErrorType
  statusCode?: number
  code?: string
  fieldErrors?: Record<string, string[]>

  constructor(type: NetworkErrorType, statusCode?: number, message?: string) {
    super(message || 'Network error')
    this.type = type
    this.statusCode = statusCode
    this.name = 'NetworkError'
  }

  /** Build a NetworkError from a parsed backend ErrorResponse + HTTP status. */
  static fromWire(statusCode: number, body: ErrorResponse): NetworkError {
    const err = new NetworkError(statusToNetworkErrorType(statusCode), statusCode, body.message)
    err.code = body.code
    err.fieldErrors = body.fieldErrors
    return err
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      statusCode: this.statusCode,
      code: this.code,
      fieldErrors: this.fieldErrors,
    }
  }
}
